import React from 'react';
import { styles } from './MenuItem.css';
import { ArrowRight } from '@jbhi-fi/lanyard-ui/components/Icons';
import { MenuDivider } from '../MenuDivider/MenuDivider';
import { MenuSubheading } from '../MenuSubheading/MenuSubheading';
import { MenuDropdown } from '../MenuDropdown/MenuDropdown';
import { MenuItemType } from '../types';

export type MenuItemProps = {
  type: MenuItemType;
  label?: string;
  leftIcon?: React.ReactNode;
  shouldHideArrow?: boolean;
  count?: number;
  isBold?: boolean;
  isActive?: boolean;
  href?: string;
  shouldOpenInNewWindow?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onBack?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  subMenuItems?: Array<MenuItemProps>;
  trackingData?: { nesting?: Array<string | undefined> };
};

const RightElement = ({
  count,
  shouldHideArrow,
  isActive,
}: {
  count?: number;
  shouldHideArrow?: boolean;
  isActive?: boolean;
}) => {
  if (!count && shouldHideArrow) return null;

  if (count) {
    return <div>{count}</div>;
  }

  return !shouldHideArrow ? (
    <span className={styles.arrow({ active: isActive })}>
      <ArrowRight height="8px" width="8px" />
    </span>
  ) : null;
};

const Wrapper = ({
  href,
  className,
  onClick,
  children,
  data_t,
  shouldOpenInNewWindow,
}: {
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  className: string;
  children: React.ReactNode;
  data_t?: string;
  shouldOpenInNewWindow?: boolean;
}) => {
  if (href) {
    return (
      <a
        role="menuitem"
        href={href}
        className={className}
        onClick={onClick}
        data-t={data_t}
        data-testid="navigation-menu-dropdown-item-link"
        target={shouldOpenInNewWindow ? '_blank' : undefined}
        rel={shouldOpenInNewWindow ? 'noreferrer' : undefined}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      role="menuitem"
      type="button"
      className={className}
      onClick={onClick}
      data-t={data_t}
      data-testid="navigation-menu-dropdown-item-button"
    >
      {children}
    </button>
  );
};

export const MenuItem = ({
  type,
  label = '',
  leftIcon,
  isBold = false,
  count,
  shouldHideArrow = false,
  isActive = false,
  onClick,
  href,
  subMenuItems,
  onBack,
  trackingData,
  shouldOpenInNewWindow,
}: MenuItemProps) => {
  if (type === MenuItemType.divider) {
    return <MenuDivider />;
  }

  if (type === MenuItemType.heading) {
    return <MenuSubheading label={label} />;
  }

  if (type === MenuItemType.headingAndDivider) {
    return (
      <>
        <MenuDivider />
        <MenuSubheading label={label} />
      </>
    );
  }

  return (
    <li className={styles.menuItem} data-testid="navigation-menu-dropdown-item">
      <Wrapper
        href={href}
        shouldOpenInNewWindow={shouldOpenInNewWindow}
        className={styles.button({
          fontWeight: isBold ? 'bold' : 'normal',
          active: isActive,
        })}
        onClick={onClick}
        data_t={label.replace(/\s/g, '')}
      >
        {leftIcon ? <div className={styles.icon}>{leftIcon}</div> : null}
        <span className={styles.label}>{label}</span>
        <RightElement shouldHideArrow={shouldHideArrow} count={count} isActive={isActive} />
      </Wrapper>
      {subMenuItems?.length ? (
        <div className={styles.subMenu} data-testid="navigation-menu-dropdown-sub-menu">
          <MenuDropdown
            menuItems={subMenuItems}
            isOpen={isActive}
            label={label}
            onBack={onBack}
            trackingData={{ ...trackingData, nesting: [...(trackingData?.nesting ?? []), label] }}
          />
        </div>
      ) : null}
    </li>
  );
};
